import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';
import { approvalQuery } from '../../../app/queries.ts';
import { ApprovalDetailScreen } from '../../../features/approval-detail/approval-detail-screen.tsx';
import { useApprovalHidesBottomBar } from '../../../features/approval-detail/viewport.ts';

/**
 * `item`: action, receipt or evidence id whose drawer is open. `decide`: `approve|reject` scrolls to the decision.
 * (`layout` is gone since 20a: the approval page is the framework page.)
 */
const Search = z.object({
  item: z.string().optional().catch(undefined),
  decide: z.enum(['approve', 'reject']).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/approvals/$approvalId')({
  validateSearch: Search,
  loader: ({ context, params }) => context.queryClient.ensureQueryData(approvalQuery(params.approvalId)),
  // The shell hides its phone bottom bar while the approval is pending: the decision bar takes its place.
  staticData: { useHideBottomBar: useApprovalHidesBottomBar },
  component: FrameworkApproval,
});

/** The framework approval page with its URL state: `?item=` (drawer) and `?decide=` (scroll and focus). */
function FrameworkApproval() {
  const { approvalId } = Route.useParams();
  const { item, decide } = Route.useSearch();
  const navigate = useNavigate({ from: '/approvals/$approvalId' });
  const router = useRouter();
  const itemDepth = useRef(0);
  useEffect(() => {
    if (!item) itemDepth.current = 0;
  }, [item]);
  const openItem = useCallback(
    (id: string) => {
      itemDepth.current += 1;
      void navigate({ search: (s) => ({ ...s, item: id }), resetScroll: false });
    },
    [navigate],
  );
  const closeItem = useCallback(() => {
    if (itemDepth.current > 0) {
      const depth = itemDepth.current;
      itemDepth.current = 0;
      router.history.go(-depth);
      return;
    }
    void navigate({ search: ({ item: _item, ...s }) => s, replace: true, resetScroll: false });
  }, [navigate, router]);
  return (
    <ApprovalDetailScreen
      key={approvalId}
      approvalId={approvalId}
      item={item}
      decide={decide}
      openItem={openItem}
      closeItem={closeItem}
    />
  );
}
